import {
  Accordion,
  Alert,
  Avatar,
  Badge,
  Card,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { formatDate } from '../../../../utils/formatDate';
import { type ActiveProductAssetsResult } from '../../../../hooks/queries/useActiveProductAssetsQuery';
import { type SessionTypeProductInfo } from '../SessionNewPage';
import { getSubscriptionExpiryInfo } from '../../../../utils/subscriptionExpiry';
import { AlertTriangle } from 'lucide-react';
import { SessionType } from '../types';

type ActiveProductsOverviewProps = {
  assets: ActiveProductAssetsResult;
  products: SessionTypeProductInfo[];
  loading: boolean;
  title: string;
  sessionType: SessionType;
};

type ProductRow = SessionTypeProductInfo & {
  videoCount: number;
  audioCount: number;
};

type AssetVideo =
  ActiveProductAssetsResult['rawProductAssets'][number]['videos'][number];

const countAudios = (videos: AssetVideo[]): number =>
  videos.reduce((accumulator, video) => {
    return accumulator + (video.audios?.length ?? 0);
  }, 0);

export function ActiveProductsOverview({
  assets,
  products,
  loading,
  title,
  sessionType,
}: ActiveProductsOverviewProps) {
  const [accordionValue, setAccordionValue] = useState<string | null>(
    'overview'
  );

  const productRows = useMemo<ProductRow[]>(() => {
    const rows = products.map(product => {
      const productAssets = assets.rawProductAssets.find(
        asset => asset.product.id === product.id
      );
      const videos = productAssets?.videos ?? [];

      return {
        ...product,
        videoCount: videos.length,
        audioCount: countAudios(videos),
      };
    });

    return rows.sort((a, b) => {
      if (a.isActive === b.isActive) {
        return a.name.localeCompare(b.name);
      }
      return a.isActive ? -1 : 1;
    });
  }, [assets.rawProductAssets, products]);

  const renderTable = () => (
    <Table highlightOnHover verticalSpacing='sm' horizontalSpacing='md' striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Applicazione</Table.Th>
          <Table.Th>Abbonamento</Table.Th>
          <Table.Th style={{ minWidth: 160 }}>Scadenza</Table.Th>
          {sessionType !== 'scenario' && (
            <>
              <Table.Th style={{ minWidth: 120 }}>Video 3D</Table.Th>
              <Table.Th style={{ minWidth: 120 }}>Audio</Table.Th>
            </>
          )}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {productRows.map(product => (
          <Table.Tr key={product.id}>
            <Table.Td>
              <Group gap='sm' wrap='nowrap'>
                <Avatar
                  src={product.image ?? undefined}
                  alt={product.name}
                  radius='sm'
                  size={40}
                  style={{
                    filter: product.isActive ? 'none' : 'grayscale(100%)',
                    opacity: product.isActive ? 1 : 0.4,
                    border: product.isActive
                      ? '1px solid var(--mantine-color-green-5)'
                      : '1px solid var(--mantine-color-gray-4)',
                  }}
                >
                  {product.name.charAt(0)}
                </Avatar>
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text
                    size='sm'
                    fw={500}
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {product.name}
                  </Text>
                  <Text size='xs' c='dimmed'>
                    {product.category ?? 'Categoria non disponibile'}
                  </Text>
                </Stack>
              </Group>
            </Table.Td>
            <Table.Td style={{ whiteSpace: 'nowrap' }}>
              <Badge
                color={product.isActive ? 'green' : 'gray'}
                variant='light'
                style={{ textTransform: 'none' }}
              >
                {product.isActive ? 'Attivo' : 'Non attivo'}
              </Badge>
            </Table.Td>
            <Table.Td>
              {product.untilDate ? (
                <Group gap={4} wrap='nowrap'>
                  <Text size='sm'>{formatDate(product.untilDate)}</Text>
                  {(() => {
                    const expiryInfo = getSubscriptionExpiryInfo(
                      product.untilDate
                    );
                    if (
                      !expiryInfo.isExpiringSoon ||
                      expiryInfo.daysUntilExpiry === null
                    ) {
                      return null;
                    }

                    const tooltipLabel =
                      expiryInfo.daysUntilExpiry <= 0
                        ? 'Subscription in scadenza'
                        : `In scadenza tra ${expiryInfo.daysUntilExpiry} giorni`;

                    return (
                      <Tooltip label={tooltipLabel} withArrow>
                        <AlertTriangle
                          size={14}
                          color='var(--mantine-color-yellow-6)'
                          aria-label={tooltipLabel}
                        />
                      </Tooltip>
                    );
                  })()}
                </Group>
              ) : (
                '-'
              )}
            </Table.Td>
            {sessionType !== 'scenario' && (
              <>
                <Table.Td>{product.videoCount || '-'}</Table.Td>
                <Table.Td>{product.audioCount || '-'}</Table.Td>
              </>
            )}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );

  const isAccordionOpen = accordionValue === 'overview';
  const buttonLabel = isAccordionOpen
    ? 'Nascondi dettagli'
    : 'Mostra maggiori dettagli';

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAccordionValue(isAccordionOpen ? null : 'overview');
  };

  return (
    <Card withBorder radius='md' p='sm' bg='gray.0'>
      <Accordion
        chevronPosition='left'
        variant='separated'
        value={accordionValue}
        onChange={setAccordionValue}
        defaultValue='overview'
      >
        <Accordion.Item value='overview'>
          <Accordion.Control>
            <Group justify='space-between' align='center'>
              <Text fw={500} size='sm'>
                {title}
              </Text>
              <Group gap='xs'>
                <UnstyledButton
                  component='span'
                  style={{
                    fontSize: 'var(--mantine-font-size-sm)',
                    padding:
                      'var(--mantine-spacing-xs) var(--mantine-spacing-sm)',
                    borderRadius: 'var(--mantine-radius-sm)',
                    transition: 'background-color 0.2s',
                  }}
                  onClick={handleButtonClick}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor =
                      'var(--mantine-color-gray-1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {buttonLabel}
                </UnstyledButton>
              </Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            {loading ? (
              <Group justify='center' py='lg'>
                <Loader size='sm' />
              </Group>
            ) : assets.error ? (
              <Alert color='red' variant='light' title='Errore nel caricamento'>
                {assets.error.message}
              </Alert>
            ) : productRows.length === 0 ? (
              <Text size='sm' c='dimmed' py='sm'>
                Nessun prodotto disponibile per questa tipologia.
              </Text>
            ) : (
              renderTable()
            )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Card>
  );
}
